import {
  getClientId,
  distributionClient,
} from "./distributionService";
import {
  CLASS_KEYS,
  emptyPeopleDistributionData,
  formatHeatmapHourRangeLabel,
  getActiveClassesFromSelection,
  getCurrentHourInTimezone,
  getDistributionTimezone,
  getLocalDateString,
  selectedClassesToApiType,
  type CameraGridItem,
  type CameraGridSiteTotals,
  type ClassKey,
  type DistributionKpis,
  type DwellTrendPoint,
  type HourlyClassPeak,
  type HourlyPeak,
  type OccupancyPoint,
  type OccupancyTrendPoint,
  type PeopleDistributionDashboardResponse,
  type PeopleDistributionData,
  type PeopleDistributionFilters,
} from "./peopleDistributionTypes";

export type DistributionCameraOption = {
  cameraId: string;
  cameraName: string;
  locationName: string;
};

export type DistributionChartsResponse = {
  summaryCards?: { title: string; value: string; subtitle: string; timestamp?: string }[];
  occupancySeries: OccupancyPoint[];
  hourlyPeaks: HourlyPeak[];
  hourlyClassPeaks: HourlyClassPeak[];
  cameraGrid?: CameraGridItem[];
  cameraGridTotal?: number;
  cameraGridSiteTotals?: CameraGridSiteTotals;
  peakHour: string;
  peakTotal: number;
  activeClasses: ClassKey[];
  throughHour?: number;
};

/** Local calendar day bounds — avoids from > to when local date is ahead of UTC. */
function dateToRange(date: string): { from: string; to: string } {
  const [year, month, day] = date.split("-").map(Number);
  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day, 23, 59, 59, 999);
  const isToday = date === getLocalDateString();
  const to = isToday ? new Date() : end;
  const fromMs = start.getTime();
  const toMs = to.getTime();
  if (fromMs > toMs) {
    return { from: to.toISOString(), to: start.toISOString() };
  }
  return { from: start.toISOString(), to: to.toISOString() };
}

function buildQueryParams(filters: PeopleDistributionFilters, availableClasses: string[]) {
  const { from, to } = dateToRange(filters.date);
  const clientId = getClientId();
  const params: Record<string, string> = {
    clientId,
    type: selectedClassesToApiType(filters.selectedClasses, availableClasses),
    date: filters.date,
    from,
    to,
    timezone: getDistributionTimezone(),
  };
  if (filters.cameraId) params.cameraId = filters.cameraId;
  return params;
}

function build1mQueryParams(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
) {
  const { from, to } = dateToRange(filters.date);
  const clientId = getClientId();
  const params: Record<string, string> = {
    clientId,
    type: selectedClassesToApiType(filters.selectedClasses, availableClasses),
    date: filters.date,
    from,
    to,
    timezone: getDistributionTimezone(),
  };
  if (filters.cameraId) params.cameraId = filters.cameraId;
  return params;
}

function parseApiData<T>(raw: unknown, fallback: T): T {
  if (raw == null) return fallback;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }
  return raw as T;
}

function parseApiDataArray<T>(raw: unknown): T[] {
  const parsed = parseApiData<T[] | null>(raw, null);
  return Array.isArray(parsed) ? parsed : [];
}

export async function getPeopleDistributionDashboardRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<PeopleDistributionDashboardResponse | null> {
  const clientId = getClientId();
  if (!clientId) return null;

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: PeopleDistributionDashboardResponse | string | null;
    }>("/distribution/dashboard", {
      params: build1mQueryParams(filters, availableClasses),
    });
    return parseApiData<PeopleDistributionDashboardResponse | null>(res.data?.data, null);
  } catch {
    return null;
  }
}

export async function getDistributionCameras(date: string): Promise<DistributionCameraOption[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  const { from, to } = dateToRange(date);

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: DistributionCameraOption[] | string;
    }>("/distribution/cameras", {
      params: { clientId, from, to, timezone: getDistributionTimezone() },
    });
    return parseApiDataArray<DistributionCameraOption>(res.data?.data);
  } catch {
    return [];
  }
}

export async function getDistributionPersonTypes(): Promise<string[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: string[] | string;
    }>("/distribution/person-type", {
      params: { clientId, timezone: getDistributionTimezone() },
    });
    return parseApiDataArray<string>(res.data?.data);
  } catch {
    return [];
  }
}

type CameraGridPageResponse = {
  cameras: CameraGridItem[];
  total: number;
  offset: number;
  limit: number;
};

export async function getCameraGridRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[],
  offset: number,
  limit?: number
): Promise<CameraGridItem[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: CameraGridPageResponse | string;
    }>("/distribution/camera-grid", {
      params: {
        ...build1mQueryParams(filters, availableClasses),
        cameraGridOffset: offset,
        cameraGridLimit: limit ?? 1000,
      },
    });
    const page = parseApiData<CameraGridPageResponse | null>(res.data?.data, null);
    return page?.cameras ?? [];
  } catch {
    return [];
  }
}

export async function getDistributionChartsRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<DistributionChartsResponse | null> {
  const clientId = getClientId();
  if (!clientId) return null;

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: DistributionChartsResponse | string;
    }>("/distribution/charts", { params: buildQueryParams(filters, availableClasses) });
    const raw = res.data?.data;
    if (!raw) return null;
    return parseApiData<DistributionChartsResponse | null>(raw, null);
  } catch {
    return null;
  }
}

export async function getDistributionKpisRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<DistributionKpis | null> {
  const clientId = getClientId();
  if (!clientId) return null;

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: DistributionKpis | string | null;
    }>("/distribution/kpis", { params: build1mQueryParams(filters, availableClasses) });
    return parseApiData<DistributionKpis | null>(res.data?.data, null);
  } catch {
    return null;
  }
}

export async function getOccupancyTrendRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<OccupancyTrendPoint[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: OccupancyTrendPoint[] | string;
    }>("/distribution/occupancy-trend", { params: build1mQueryParams(filters, availableClasses) });
    return parseApiDataArray<OccupancyTrendPoint>(res.data?.data);
  } catch {
    return [];
  }
}

export async function getDwellTrendRest(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<DwellTrendPoint[]> {
  const clientId = getClientId();
  if (!clientId) return [];

  try {
    const res = await distributionClient.get<{
      success?: boolean;
      data?: DwellTrendPoint[] | string;
    }>("/distribution/dwell-trend", { params: build1mQueryParams(filters, availableClasses) });
    return parseApiDataArray<DwellTrendPoint>(res.data?.data);
  } catch {
    return [];
  }
}

export async function getPeopleDistributionDataFromApi(
  filters: PeopleDistributionFilters,
  availableClasses: string[]
): Promise<PeopleDistributionData> {
  const clientId = getClientId();
  if (!clientId) {
    return emptyPeopleDistributionData(filters.selectedClasses);
  }

  const timezone = getDistributionTimezone();
  const classList = availableClasses.length ? availableClasses : CLASS_KEYS;

  const dashboard = await getPeopleDistributionDashboardRest(filters, classList);
  if (!dashboard) {
    return emptyPeopleDistributionData(filters.selectedClasses);
  }

  const activeClasses =
    dashboard.activeClasses?.length
      ? dashboard.activeClasses
      : getActiveClassesFromSelection(filters.selectedClasses);

  const throughHour =
    filters.date === getLocalDateString()
      ? getCurrentHourInTimezone(timezone)
      : 23;

  return {
    summaryCards: [],
    kpis: dashboard.kpis,
    occupancySeries: [],
    occupancyTrend: dashboard.occupancyTrend,
    dwellTrend: dashboard.dwellTrend,
    hourlyPeaks: [],
    hourlyClassPeaks: [],
    hourlyHeatmap: dashboard.hourlyHeatmap ?? [],
    heatmapHours: dashboard.heatmapHours ?? [],
    cameraGrid: dashboard.cameraGrid,
    cameraGridTotal: dashboard.cameraGridTotal,
    cameraGridSiteTotals: dashboard.cameraGridSiteTotals,
    peakHour: "",
    peakTotal: dashboard.kpis.peakOccupancy,
    peakDwellBucketTime: dashboard.kpis.peakDwellBucketTime,
    throughHour: dashboard.throughHour ?? throughHour,
    activeClasses,
    asOf: dashboard.asOf,
  };
}

export function formatHeatmapHourLabel(hour: string): string {
  return formatHeatmapHourRangeLabel(hour);
}
